'use client';
import { useCallback, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { TestimonialItem } from '@/components/pages/TestimonialsPage/Testimonials/TestimonialItem/TestimonialItem';
import { CreateReviewModal } from '@/components/features/CreateReviewModal/CreateReviewModal';
import { ShowMoreButton } from '@/components/ui/ShowMoreButton/ShowMoreButton';
import { Pagination } from '@/components/features/Pagination/Pagination';
import { ActionButton } from '@/components/ui/ActionButton/ActionButton';
import { Search } from '@/components/ui/form/Search/Search';
import { Loader } from '@/components/ui/Loader/Loader';
import Modal from '@/components/layouts/Modal/Modal';
import { getTestimonialsList } from '@/api/testimonials/testimonials';
import { Tab, Tabs } from '@/components/features/Tabs/Tabs';
import { ITestimonialItem, TestimonialType } from '@/api/testimonials/testimonials.types';
import s from '@/components/pages/TestimonialsPage/TestimonialsPage.module.scss';

const PER_PAGE = 5;
const testimonialsTabs = [
  {
    name: 'Все отзывы',
    key: null,
  },
  {
    name: 'Врачи',
    key: TestimonialType['Отзыв о враче'],
  },
  {
    name: 'О клинике',
    key: TestimonialType['Отзыв о клинике'],
  },
];

interface Props {
  currentPage: number;
  query: string;
  testimonialType: string;
}

export const Testimonials = ({ currentPage, query, testimonialType }: Props) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const [cards, setCards] = useState<ITestimonialItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [totalPages, setTotalPages] = useState(0);

  const correctType: TestimonialType | null = testimonialType ? JSON.parse(testimonialType) : null;

  useEffect(() => {
    if (!searchParams.has('show_more')) return;

    const params = new URLSearchParams(searchParams);
    params.delete('show_more');
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, [searchParams]);

  const fetchTestimonials = async () => {
    if (searchParams.get('show_more') === 'true') return;

    setIsLoading(true);

    const testimonials = await getTestimonialsList({
      page: currentPage,
      search: query,
      per_page: PER_PAGE,
      testimonial_type: correctType,
    });

    if (!testimonials) {
      setIsLoading(false);
      return;
    }

    setCards(testimonials.items);
    setTotalPages(testimonials.pages);
    setIsLoading(false);
  };

  const showMore = useCallback(async () => {
    if (currentPage >= totalPages) return;

    const params = new URLSearchParams(searchParams);
    const nextPage = currentPage + 1;
    params.set('show_more', String(true));
    params.set('page', String(nextPage));

    const testimonials = await getTestimonialsList({
      page: nextPage,
      search: query,
      per_page: PER_PAGE,
      testimonial_type: correctType,
    });

    setCards((prev) => [...prev, ...testimonials.items]);
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, [totalPages, currentPage]);

  const onChangeTab = (activeTab: Tab<TestimonialType | null>) => {
    const params = new URLSearchParams(searchParams);
    const newValue = JSON.stringify(activeTab.key);

    params.set('testimonial_type', newValue);
    params.set('page', String(1));
    params.delete('query');
    params.delete('show_more');

    replace(`${pathname}?${params.toString()}`);
  };

  return (
    <>
      {isLoading && <Loader fixed />}
      <div>
        <div className={s.testimonials__tabsWrapper}>
          <Tabs tabs={testimonialsTabs} activeTabKey={correctType} setActiveTab={onChangeTab} />
          <ActionButton className={s.testimonials__button} onClick={() => setIsOpenCreate(true)}>
            Оставить отзыв
          </ActionButton>
        </div>

        {correctType === TestimonialType['Отзыв о враче'] && (
          <Search
            className={s.testimonials__search}
            placeholder={'Введите ФИО врача или специализацию'}
          />
        )}

        <ul className={s.testimonials__list}>
          {cards.map((card) => {
            return (
              <li key={card.id}>
                <TestimonialItem testimonial={card} abbreviated />
              </li>
            );
          })}
        </ul>

        <div className={s.testimonials__navs}>
          {cards.length > 0 && currentPage < totalPages && (
            <ShowMoreButton
              onClick={showMore}
              disabled={totalPages < 2}
              className={s.testimonials__showMore}
            />
          )}

          <Pagination totalPages={totalPages} />
        </div>
      </div>

      <Modal isOpen={isOpenCreate} onClose={() => setIsOpenCreate(false)}>
        <CreateReviewModal onSuccess={() => setIsOpenCreate(false)} />
      </Modal>
    </>
  );
};